import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import {openTicketingComingSoonModal} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';

// Keep in sync with App\Libraries\Facades\Event\EventTicketing\Enums\TicketingBlockedReason (backend is canonical).
export enum TicketingBlockedReason {
  PLAN = 'plan',
  COUNTRY = 'country',
  FEATURE = 'feature',
}

// The backend reads acceptTerms as a string and compares it to '1' — a boolean would not match.
const ACCEPT_TERMS_PARAM_VALUE = '1';

interface TicketingOnboardingLink {
  isAllowed: boolean;
  blockedReason: TicketingBlockedReason | null;
  needsOnboarding: boolean;
  // false = never submitted (initial CTA); true = submitted (isVerifying then splits verifying vs needs-more).
  detailsSubmitted: boolean;
  // true = details submitted and Stripe is verifying (nothing due, show "please wait"); false = action required.
  isVerifying: boolean;
  // Whether the current Events & Ticketing terms version is on record for this organiser.
  hasAcceptedTerms: boolean;
  connectUrl: string | null;
}

/**
 * Asks the backend whether the organiser still needs Stripe onboarding and, if so, returns the
 * Stripe-hosted Account Link URL to send them to. Hi.Events owns the connected account and mints the
 * link; we just relay it.
 *
 * Pass acceptTerms on any call made while the organiser's consent box is ticked — the Connect click and
 * the panel's recheck. The backend records it and withholds the link until it has. Calls made before
 * the organiser can have consented (the wizard gate, the verifying poll) pass nothing.
 */
export const fetchTicketingOnboardingLink = async (acceptTerms = false): Promise<TicketingOnboardingLink> => {
  const params = acceptTerms ? {acceptTerms: ACCEPT_TERMS_PARAM_VALUE} : {};
  return (await window.PMW.writeLocal('eventticketing/getTicketingOnboardingLink', params)) as TicketingOnboardingLink;
};

/**
 * The user-facing message the backend attached to a failed onboarding call, or null when it carried none.
 *
 * writeLocal rejects with an Error whose message is the ticketing error envelope's user message, so a
 * refusal the organiser can act on (their consent could not be recorded) arrives already worded. A
 * network drop or a parse failure has no such message and falls back to the panel's generic copy.
 */
export const getTicketingOnboardingErrorMessage = (error: unknown): string | null => {
  return error instanceof Error && error.message !== '' ? error.message : null;
};

/**
 * Hard onboarding gate for the step 2→3 transition on paid ticketing. The verdict is server-side
 * because window globals can be stale after an AJAX login. If the organiser is not allowed
 * (plan/country), opens the matching dialog and returns. If allowed and already onboarded, calls
 * onComplete immediately (advance). If allowed but still needs onboarding, opens the onboarding dialog
 * — passing onComplete so the dialog advances to Review itself once onboarding is confirmed (it mints a
 * fresh Stripe Account Link lazily on click, so the connectUrl in the response is unused here). Rejects
 * on a failed check so the caller can keep the user on step 2.
 */
export const runTicketingOnboardingGate = async (onComplete: () => void): Promise<void> => {
  const onboarding = await fetchTicketingOnboardingLink();
  if (!onboarding.isAllowed) {
    if (onboarding.blockedReason === TicketingBlockedReason.COUNTRY || onboarding.blockedReason === TicketingBlockedReason.FEATURE) {
      openTicketingComingSoonModal(onboarding.blockedReason === TicketingBlockedReason.COUNTRY);
    } else {
      openEventsUpsellDialog(EventUpsellType.TICKETING, undefined);
    }
    return;
  }
  if (!onboarding.needsOnboarding) {
    onComplete();
    return;
  }
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.TICKETING_STRIPE_ONBOARDING_MODAL,
      panelProps: {
        onComplete,
        detailsSubmitted: onboarding.detailsSubmitted,
        isVerifying: onboarding.isVerifying,
        hasAcceptedTerms: onboarding.hasAcceptedTerms,
      },
    })
  );
};
